import { TextField } from "@mui/material";
import { DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { CalendarOrClockPickerView } from "@mui/x-date-pickers/internals/models";
import dayjs, { Dayjs } from "dayjs";
import { useEffect } from "react";
import { FieldValues, UseFormRegister, UseFormWatch } from "react-hook-form";

interface Props {
  register?: UseFormRegister<FieldValues>;
  watch: UseFormWatch<FieldValues>;
  onChange: (value: Dayjs | null) => void;
  label: string;
  required?: boolean | string;
  id: string;
  sx?: any;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  disallow_future_date?: boolean;
  views?: CalendarOrClockPickerView[];
  inputFormat?: string;
  custom_validation?: (value: any, form_values: any) => boolean | string;
}

const DEFAULT_VIEWS: CalendarOrClockPickerView[] = ["year", "month", "day", "hours", "minutes", "seconds"];
const DEFAULT_INPUT_FORMAT = "DD/MM/YYYY HH:mm:ss";

export const DateTimePickerField: React.FC<Props> = ({
  onChange,
  label,
  register,
  id,
  required,
  watch,
  sx,
  disabled = false,
  error = false,
  helperText = "",
  disallow_future_date = false,
  views = DEFAULT_VIEWS,
  inputFormat = DEFAULT_INPUT_FORMAT,
  custom_validation,
}) => {
  const value = watch(id);
  // console.log("value", value);

  function isDateTimeValid(value: any, form_values: any) {
    if (!required && !value) {
      return true;
    }

    if (!value) {
      return "Date can't be blank";
    }
    if (!value?.isValid()) {
      let error_message = "Date format is incorrect, required format is DD/MM/YYYY";
      if (views.includes("hours") && views.includes("minutes")) {
        return "Date format is incorrect, required format is DD/MM/YYYY hh:mm";
      }
      return error_message;
    }
    if (disallow_future_date && !dayjs().isAfter(value)) {
      return "Date can't be more than current date/time";
    }
    if (custom_validation) {
      return custom_validation(value, form_values);
    }
    return true;
  }

  useEffect(() => {
    if (register) {
      register(id, { required, validate: isDateTimeValid });
    }
  }, []);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
      <DateTimePicker
        label={label}
        value={value}
        onChange={onChange}
        renderInput={(params) => <TextField size="small" {...params} error={error} helperText={helperText} />}
        inputFormat={inputFormat}
        views={views}
      />
    </LocalizationProvider>
  );
};
